<template>
  <v-dialog v-model="show" max-width="760" scrollable>
    <v-card>
      <v-card-title class="text-h6">
        {{ title }}
        <v-spacer />
        <v-btn icon small @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="lines"
          :loading="loading"
          :loading-text="$t('finance_loading')"
          :no-data-text="$t('finance_no_statement_lines')"
          mobile-breakpoint="0"
          hide-default-footer
          disable-pagination
          dense
        >
          <template #item.date="{ item }">{{ localeDate(item.date) }}</template>
          <template #item.description="{ item }">{{ translate(item.description) }}</template>
          <template #item.order="{ item }">
            <a v-if="item.orderId" class="order-link" @click.stop="$emit('open-order', item.orderId)">
              #{{ item.orderId }}
            </a>
            <span v-else>—</span>
          </template>
          <template #item.rate="{ item }">{{ item.rate || "—" }}</template>
          <template #item.amount="{ item }">
            <span class="font-weight-bold" :style="{ color: item.amount >= 0 ? positive : negative }">
              {{ fmt(item.amount, true) }}
            </span>
          </template>
        </v-data-table>
        <div v-if="total > lines.length" class="pa-3 text-caption grey--text">
          {{ $t("finance_statement_lines_truncated", { shown: lines.length, total }) }}
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import { FLOW_COLORS, formatMoney } from "./constants";

// Redovi iz kojih je nastao jedan broj razrade: GET /statement/lines?category&from&to.
// Oblik reda nije striktno propisan - mapiranje je tolerantno (vidi docs, "Претпоставке").
const RATE_SUFFIX = { 1: "/mj", 2: "%", 3: "/obrok", 4: "" };

const mapLine = (dto, currency) => {
  const rateValue = dto.rate_value != null ? Number(dto.rate_value) : null;
  let rate = dto.rate || null;
  if (!rate && rateValue != null) {
    rate =
      Number(dto.rate_type) === 2
        ? `${rateValue}%`
        : `${formatMoney(rateValue, currency)}${RATE_SUFFIX[dto.rate_type] || ""}`;
  }
  const orderId = dto.order_id ?? (dto.source_type === "ORDER" ? dto.source_id : null);
  return {
    id: dto.id,
    date: dto.date || dto.created_at,
    description: dto.description || dto.note || dto.title || "",
    orderId: orderId || null,
    rate,
    amount: Number(dto.amount) || 0,
  };
};

export default {
  name: "StatementLinesDialog",

  mixins: [hasSnackbar],

  props: {
    value: { type: Boolean, default: false },
    restaurantId: { type: [Number, String], default: null },
    category: { type: String, default: "" },
    label: { type: String, default: "" },
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    currency: { type: String, default: "" },
  },

  data: () => ({
    loading: false,
    lines: [],
    total: 0,
    positive: FLOW_COLORS.positive,
    negative: FLOW_COLORS.negative,
  }),

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    title() {
      return `${this.translate(this.label)} · ${this.localeDate(this.from)} - ${this.localeDate(this.to)}`;
    },
    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_description"), value: "description", sortable: false },
        { text: this.$t("finance_order"), value: "order", sortable: false },
        { text: this.$t("finance_rate_label"), value: "rate", sortable: false },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
      ];
    },
  },

  watch: {
    value(isOpen) {
      if (isOpen) this.load();
    },
  },

  methods: {
    fmt(value, signed) {
      return formatMoney(value, this.currency, { signed });
    },

    async load() {
      if (!this.restaurantId || !this.category) return;
      this.loading = true;
      this.lines = [];
      try {
        const { data } = await restaurantFinanceService.getStatementLines(this.restaurantId, {
          category: this.category,
          from: this.from,
          to: this.to,
        });
        const rows = data?.data || data?.lines || [];
        this.lines = rows.map((dto) => mapLine(dto, this.currency));
        this.total = data?.meta?.total ?? this.lines.length;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_statement_lines_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.order-link {
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
}
</style>
